import { useEffect, useRef, useState, useCallback } from "react";
import { Send } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n";
import { useAuth } from "@/AuthContext";
import { toast } from "sonner";
import api, { apiError } from "@/api";

export function ChatDialog({ open, onOpenChange, jobId, craftsmanUserId, title }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef(null);
  const lastRead = useRef("");

  const load = useCallback(async () => {
    if (!jobId || !craftsmanUserId) return;
    const { data } = await api.get(`/jobs/${jobId}/thread`, { params: { craftsman_user_id: craftsmanUserId } });
    setMsgs(data);
    const last = data.length ? data[data.length - 1].created_at : "";
    if (last && lastRead.current !== last) {
      lastRead.current = last;
      api.post("/threads/read", { job_id: jobId, craftsman_user_id: craftsmanUserId })
        .then(() => window.dispatchEvent(new Event("mm:unread"))).catch(() => {});
    }
  }, [jobId, craftsmanUserId]);

  useEffect(() => {
    if (!open) return;
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [open, load]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await api.post(`/jobs/${jobId}/messages`, { text, craftsman_user_id: craftsmanUserId });
      setText("");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display">{title || t("chat.title")}</DialogTitle></DialogHeader>
        <div className="h-80 overflow-y-auto space-y-2 py-2 pr-1" data-testid="chat-messages">
          {msgs.map((m) => {
            const mine = user && m.sender_user_id === user._id;
            return (
              <div key={m._id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-[#C65338] text-white" : "bg-[#F2E8DF] text-[#3A2E28]"}`}>
                  {!mine && <div className="text-xs font-semibold opacity-70 mb-0.5">{m.sender_name}</div>}
                  {m.kind === "offer" && <div className="text-[10px] font-bold uppercase tracking-wider opacity-70 mb-0.5">{t("chat.offer")}</div>}
                  <span className="whitespace-pre-wrap">{m.text}</span>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
        <p data-testid="guard-hint-chat" className="text-xs text-[#8C7A70]">{t("guard.hintChat")}</p>
        <form onSubmit={send} className="flex items-center gap-2 pt-2">
          <Input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder={t("chat.placeholder")} className="rounded-full" />
          <Button data-testid="chat-send" type="submit" size="icon" className="rounded-full bg-[#C65338] hover:bg-[#A93F25] text-white shrink-0"><Send className="w-4 h-4" /></Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}